import type { ApolloClient } from '@apollo/client'
import { type ViewQueryList, withProperty } from '@salescore/core'
import { Array, Filter, Order, pipe, Record, Result } from 'effect'
import { isNotNullish, isNotUndefined, isUndefined } from 'effect/Predicate'
import { t } from 'i18next'

import type { RelationChange } from '../../recoil/records/mutations/updateRecordNodeRelations'
import type { NewRecordChange } from '../../rsheet/action/addNewRecord/addNewRecords'
import { getReferencedStreamName } from '../../rsheet/util/getReferencedStreamName'
import { getRecordNodeWithInnerRowIndex } from '../../rsheet/util/innerRowRelated'
import type { RecordChange } from '../../rsheet/util/updateViewRecords'
import type { RSheetColumn, RSheetRecordNode } from '../column'
import { adjustRecordHeights } from '../copyAndPaste/adjustRecordHeights'
import { type CopyMatrix, isPlaceholderCellValue } from '../copyMatrix'
import type { RSheetsCursor } from '../cursor/cursorTypes'
import type { MagnifyingGlassChange } from '../useUpsertViewRecordsEntry/applyMagnifyingGlassChange'
import { type ChangeCandidate, generateChangeCandidates } from './generateChangeCandidates'
import { generateMagnifyingGlassChanges } from './generateMagnifyingGlassChanges'
import { generateNewRecordChanges } from './generateNewRecordChanges'
import { generateParentToChildRelationChanges } from './generateParentToChildRelationChanges'
import { generateUpdateChange } from './generateUpdateChange'
import type { RelationPaste } from './relationPaste'

interface Argument {
  copyMatrix: CopyMatrix
  records: RSheetRecordNode[]
  columns: Array<RSheetColumn<RSheetRecordNode>>
  cursor: RSheetsCursor | undefined
  query: ViewQueryList | undefined
  organizationId: string
  client: ApolloClient<object>
}

interface Result {
  newRecordChanges: NewRecordChange[]
  recordChanges: RecordChange[]
  relationChanges: RelationChange[]
  magnifyingGlassChanges: MagnifyingGlassChange[]
  errors: Array<{ message: string }>
  recordsAfterHeightAdjustment: RSheetRecordNode[]
}

/**
 * コピー情報から UpsertViewRecordsEntry に渡す変更情報を生成する関数
 */
export const generateChangesFromCopy = async (argument: Argument): Promise<Result> => {
  // query が undefined なのは kpi/kpiPivot view 経由で本関数が呼ばれた場合のみ。
  // 呼び出し元 usePasteCopyMatrix は sheet view 専用処理のため現実には到達しないが、
  // 配下の sub function (generateMagnifyingGlassChanges 等) で個別に undefined ハンドル
  // するより、冒頭で一括して空結果を返す方が意図が明確になるため早期 return する。
  if (argument.query === undefined) {
    return {
      newRecordChanges: [],
      recordChanges: [],
      relationChanges: [],
      magnifyingGlassChanges: [],
      errors: [],
      recordsAfterHeightAdjustment: argument.records,
    }
  }
  const { updates, inserts } = generateChangeCandidates({
    ...argument,
    getReferencedStreamName: (column) => getReferencedStreamName(column, argument.query?.tree),
  })
  const recordsAfterHeightAdjustment = pipe(
    updates,
    Array.filter((a) => withProperty(a, 'recordId')),
    Array.dedupeWith(
      (a, b) => a.recordId === b.recordId && a.column.node.path.join(',') === b.column.node.path.join(','),
    ),
    (_updates) => adjustRecordHeights(argument.records, _updates),
  )
  // カラムのノードごとに changeCandidates をグルーピングして、浅いものから順に処理するようにする
  const changeCandidateGroupsByNode = pipe(
    [...updates, ...inserts],
    Array.groupBy(({ column }) => column.node.path.join(',')),
    Record.values,
    Array.sortWith(([candidate]) => candidate.column.node.path.length, Order.Number),
  )

  const result: Result = {
    newRecordChanges: [],
    recordChanges: [],
    relationChanges: [],
    magnifyingGlassChanges: [],
    errors: [],
    recordsAfterHeightAdjustment,
  }
  for (const changeCandidates of changeCandidateGroupsByNode) {
    const partialResult = await generateRecordChangesForColumnNode(
      {
        ...argument,
        records: recordsAfterHeightAdjustment,
      },
      changeCandidates,
      result.relationChanges,
    )
    result.newRecordChanges.push(...partialResult.newRecordChanges)
    result.recordChanges.push(...partialResult.recordChanges)
    result.relationChanges.push(...partialResult.relationChanges)
    result.magnifyingGlassChanges.push(...partialResult.magnifyingGlassChanges)
    result.errors.push(...partialResult.errors)
  }
  return result
}

const generateRecordChangesForColumnNode = async (
  { records, query, client, organizationId }: Argument,
  changeCandidates: Array.NonEmptyArray<ChangeCandidate>,
  relationChangesSoFar: RelationChange[],
): Promise<Omit<Result, 'recordsAfterHeightAdjustment'>> => {
  const filteredChangeCandidates = pipe(
    changeCandidates,
    Array.filter((changeCandidate) => !isNullNoopForMissingRecord(changeCandidate)),
  )
  const [firstFilteredChangeCandidate] = filteredChangeCandidates
  if (firstFilteredChangeCandidate === undefined) {
    return {
      newRecordChanges: [],
      recordChanges: [],
      relationChanges: [],
      magnifyingGlassChanges: [],
      errors: [],
    }
  }
  const activeChangeCandidates: Array.NonEmptyArray<ChangeCandidate> = [
    firstFilteredChangeCandidate,
    ...filteredChangeCandidates.slice(1),
  ]

  const { proceedAsIs, joinKeyPastingToChild, lackingJoinKeyValue } = classifyChangeCandidates(
    query,
    records,
    activeChangeCandidates,
    relationChangesSoFar,
  )
  const joinErrors =
    lackingJoinKeyValue.length > 0
      ? [
          {
            message: t(`結合キーに対して値が設定されていないため、セルにペーストできなかったレコードがあります。`),
          },
        ]
      : []

  const magnifyingGlassChangeCandidates = joinKeyPastingToChild.flatMap(({ joinKeyCandidate }) => joinKeyCandidate) // 名前フィールドへのペースト
  const nonMagnifyingGlassChangeCandidates = [
    ...proceedAsIs.flat(),
    ...joinKeyPastingToChild.flatMap(({ group }) => group), // 名前フィールド以外へのペースト
  ]

  // STEP 1: 虫眼鏡セルへのペーストについて magnifyingGlassChanges を生成する
  const { errors: magnifyingGlassErrors, magnifyingGlassChanges } = await generateMagnifyingGlassChanges(
    magnifyingGlassChangeCandidates,
    query,
    organizationId,
    client,
  )

  // STEP 2: 虫眼鏡以外の変更について RecordChanges を生成する
  const [nonMagnifyingGlassChanges, nonMagnifyingGlassErrors] = pipe(
    nonMagnifyingGlassChangeCandidates,
    Array.map(({ column, value, relationPaste, rowIndex, innerRowIndex }) =>
      generateUpdateChange({ column, value, relationPaste, rowIndex, innerRowIndex }),
    ),
    Array.separate,
  )

  // STEP 2.5: RecordChanges を参照項目とそれ以外に分ける
  const [recordChangesWithRelation, nonRelationChanges] = pipe(
    nonMagnifyingGlassChanges,
    Array.partition(
      Filter.fromPredicate(
        (rc): rc is RecordChange & { metaType: 'relation'; relationPaste: RelationPaste | undefined } =>
          rc.metaType === 'relation', // 親ブロックに存在する、子ブロックのレコード ID を設定する参照項目へのペースト
      ),
    ),
  )

  // STEP 3: 参照項目へのペーストについて relationChanges を生成する
  const {
    recordChanges,
    relationChanges,
    relationChangeKeySet,
    errors: relationErrors,
  } = await generateParentToChildRelationChanges(recordChangesWithRelation, query, client, organizationId)

  // STEP 4: 新規作成するレコードの情報を生成する
  const { newRecordChanges, defaultValueChanges } = generateNewRecordChanges(
    nonMagnifyingGlassChangeCandidates,
    records,
    relationChangeKeySet,
    query,
  )

  return {
    newRecordChanges,
    recordChanges: [...defaultValueChanges, ...nonRelationChanges, ...recordChanges],
    relationChanges,
    magnifyingGlassChanges,
    errors: [...joinErrors, ...nonMagnifyingGlassErrors, ...magnifyingGlassErrors, ...relationErrors].filter(
      isNotUndefined,
    ),
  }
}

const isNullNoopForMissingRecord = (changeCandidate: ChangeCandidate): boolean => {
  if (!isPlaceholderCellValue(changeCandidate.value)) {
    return false
  }
  if ('recordId' in changeCandidate) {
    return changeCandidate.recordId === undefined
  }
  return true
}

// 更新対象の子レコードが、既に親レコードに結合されているか否かを判定する
export const areRecordsJoined = (
  query: ViewQueryList | undefined,
  records: RSheetRecordNode[],
  changeCandidateGroupsByRecord: Array.NonEmptyArray<ChangeCandidate>,
): boolean => {
  const [representative] = changeCandidateGroupsByRecord
  const { column } = representative
  // 子レコードの親レコードとの関係が 1:N の 1 の方であることを確認する。そうでなければ結合済みと見做す。
  if (column.nodeType !== 'parent') {
    return true
  }

  const rootRecord = records[representative.rowIndex]
  // ルートレコードが存在しない場合は結合されていない
  if (rootRecord === undefined) {
    return false
  }
  const result = getRecordNodeWithInnerRowIndex(rootRecord, column.node, representative.innerRowIndex)
  // ペースト先にセルがない場合は結合しない（空セルも、値が入っていないだけでセルとしては存在するのでこんな場合はないはず）
  if (result === undefined) {
    return false
  }
  // ペースト先のセルに既にレコードが存在する場合は結合済み
  return isNotUndefined(result.recordNode)
}

interface ClassifyChangeCandidatesResult {
  proceedAsIs: Array<Array.NonEmptyArray<ChangeCandidate>>
  joinKeyPastingToChild: Array<{
    joinKeyCandidate: ChangeCandidate
    group: ChangeCandidate[]
  }>
  lackingJoinKeyValue: Array<Array.NonEmptyArray<ChangeCandidate>>
}

const classifyChangeCandidates = (
  query: ViewQueryList | undefined,
  records: RSheetRecordNode[],
  changeCandidates: Array.NonEmptyArray<ChangeCandidate>,
  relationChangesSoFar: RelationChange[],
): ClassifyChangeCandidatesResult => {
  // レコードごとに changeCandidates をグルーピングして処理する
  const changeCandidateGroupsByRecord = pipe(
    changeCandidates,
    Array.groupBy(({ rowIndex, innerRowIndex }) => `${rowIndex}-${innerRowIndex}`),
    Record.values,
  )

  // まず、親レコードと結合されているかどうかで分ける
  const [alreadyJoined, notJoinedYet] = pipe(
    changeCandidateGroupsByRecord,
    Array.partition(Filter.fromPredicate((group) => areRecordsJoined(query, records, group))),
  )

  // 続いて、親ノードの joinKey に対して値がペーストされるかどうかを確認する
  const relationChangesSoFarSet = new Set(
    relationChangesSoFar.map((rc) => `${rc.node.path.join(',')}-${rc.rowIndex}-${rc.innerRowIndex}`),
  )
  const [joinKeyPastedToParent, notJoinKeyPastedToParent] = pipe(
    notJoinedYet,
    Array.partition(
      Filter.fromPredicate(([representative]) =>
        relationChangesSoFarSet.has(
          `${representative.column.node.path.join(',')}-${representative.rowIndex}-${representative.innerRowIndex}`,
        ),
      ),
    ),
  )

  // 最後に、子ノードの joinKey のセルにペーストで値が入るかどうかを確認する
  const [joinKeyPastingToChild, notJoinKeyPastingToChild] = pipe(
    notJoinKeyPastedToParent,
    Array.partition((group) => {
      const [representative] = group
      const joinKeyPropertyName = representative.column.node.read.join?.search.dependedPropertyNames[0]?.propertyName
      // 子ノードに結合キーが存在しない場合は失敗とする（おそらくないはず）
      if (joinKeyPropertyName === undefined) {
        return Result.fail(group)
      }
      // 結合キーに対して値がペーストされる場合は成功とする
      const joinKeyCandidate = group.find(
        (changeCandidate) =>
          changeCandidate.column.configField?.property.propertyName === joinKeyPropertyName &&
          isNotNullish(changeCandidate.value),
      )
      if (isUndefined(joinKeyCandidate)) {
        return Result.fail(group)
      }
      return Result.succeed({ joinKeyCandidate, group: group.filter((item) => item !== joinKeyCandidate) })
    }),
  )

  return {
    proceedAsIs: [...alreadyJoined, ...joinKeyPastedToParent],
    joinKeyPastingToChild,
    lackingJoinKeyValue: notJoinKeyPastingToChild,
  }
}
